/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

(function() {
    //console.log('loaded custom.js')

    async function getCart() {
        const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
        return res.json();
    }

    let gwp_progress = false

    document.addEventListener('on:cart:add', async (event) => {
        gwp_progress = true;
        const cart = await getCart();
        onCartUpdated(cart);
    });

    document.addEventListener('on:cart-drawer:after-open', async (event) => {
        if (gwp_progress == false) {
            gwp_progress = true;
            const cart = await getCart();
            onCartUpdated(cart);
        }
    });

    document.addEventListener('on:cart:after-merge', async (event) => {
        gwp_progress = true;
        const cart = await getCart();
        onCartUpdated(cart);
    });

    function onCartUpdated(cart) {
        // Convert theme setting to an integer in cents
        const gwp_min = Math.round(
            parseFloat(window.theme.settings.gwp_min) * 100
        ) || 0;
        const gwp_product = window.theme.settings.gwp_product;
        const discountConfig = window.theme.customDiscount || {};
        const discountStatus = discountConfig.status || '';
        const discountMethod = discountConfig.method || '';
        const discountCode = (discountConfig.code || '').toUpperCase();

        const hasDiscountCode = Array.isArray(cart.discount_codes) &&
            cart.discount_codes.some((entry) =>
                (entry.code || '').toUpperCase() === discountCode
            );
        const discountIsEnabled = discountStatus === 'ACTIVE' && (
            discountMethod === 'AUTOMATIC' ||
            (discountMethod === 'CODE' && discountCode && hasDiscountCode)
        );

        if (gwp_product && discountIsEnabled) {
            const variantId = gwp_product.first_variant_id;

            let qualifyingSubtotal = cart.items_subtotal_price || 0;
            let hasGift = false;

            for (const item of cart.items || []) {
                if (item.variant_id === variantId) {
                    qualifyingSubtotal -= item.original_line_price || item.final_line_price || 0;
                    hasGift = true;
                }
            }

            if (qualifyingSubtotal >= gwp_min && !hasGift && gwp_product.available) {
                fetch('/cart/add.js', {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    id: variantId,
                    quantity: 1,
                    properties: { _gwp: 'true' }
                })
                })
                .then(r => r.json())
                .then(() => {
                    // Refresh cart
                    gwp_progress = false;
                    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
                        bubbles: true
                    }));
                })
                .catch(err => console.warn('GWP add failed', err));
            }
        }
    }



  if (window.customDiscountTiersLoaded) return;
  window.customDiscountTiersLoaded = true;

  function readCartStorage() {
    try {
      var raw = localStorage.getItem('cart');

      if (!raw) {
        return null;
      }

      return JSON.parse(raw);
    } catch (error) {
      return null;
    }
  }

  function hasMatchingDiscount(cartData, discountCode) {
    if (!cartData || !Array.isArray(cartData.discount_codes)) {
      return false;
    }

    return cartData.discount_codes.some(function (entry) {
      return (entry.code || '').toUpperCase() === discountCode;
    });
  }

  function updateCustomDiscountTiers(scope) {
    var root = scope || document;
    var containers = root.querySelectorAll('[data-custom-discount-tiers]');
    var discountConfig = window.theme.customDiscount || {};
    var status = discountConfig.status || '';
    var method = discountConfig.method || '';
    var code = (discountConfig.code || '').toUpperCase();

    // No tier container on collection pages etc — nothing to update.
    // This avoids breaking the rest of theme.js.
    if (!containers.length) {
      return;
    }

    var cartData = readCartStorage();

    containers.forEach(function (container) {
      if (status !== 'ACTIVE') {
        return;
      }

      if (method !== 'CODE') {
        return;
      }

      if (!code) {
        return;
      }

      if (hasMatchingDiscount(cartData, code)) {
        container.style.display = '';
      }
    });
  }

  function initCustomDiscountTiers() {
    updateCustomDiscountTiers(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCustomDiscountTiers);
  } else {
    initCustomDiscountTiers();
  }

  document.addEventListener('shopify:section:load', function (event) {
    updateCustomDiscountTiers(event.target);
  });

  window.addEventListener('storage', function (event) {
    if (event.key === 'cart') {
      updateCustomDiscountTiers(document);
    }
  });

})();
